import dayjs from "dayjs";
import { useRouter } from "next/router";
import { PrimeIcons } from "primereact/api";
import { Button } from "primereact/button";
import { useTranslation } from "react-i18next";
import { useTrackEvent } from "../../../../common/api/useTrackEvent";
import { Card } from "../../../../common/components/Card";
import {
    ADMIN_ROUTES,
    REDIRECT_ROUTES,
    ZENDESK_URLS,
} from "../../../../common/constants";
import { ANALYTICS_EVENTS } from "../../../../common/constants/analytics.constants";
import { useRedirect } from "../../../../common/hooks/useRedirect";
import { useUIStore } from "../../../../common/store/useUIStore";
import { IconBetterspace } from "../Icons";
import { AdminControls } from "./AdminControls";
import { CompanyControls } from "./CompanyControls";
import { LocationControls } from "./LocationControls";

interface SidebarProps {
    isOpen: boolean;
    onCloseIconClick?: () => void;
}

const Sidebar = (props: SidebarProps) => {
    const { isOpen, onCloseIconClick = () => {} } = props;

    const { trackEvent } = useTrackEvent();

    const { t } = useTranslation(["common", "energy"]);

    const { theme } = useUIStore();

    const { triggerZendeskRedirect } = useRedirect();

    const router = useRouter();
    const { companyId, locationId } = router.query as {
        companyId?: string;
        locationId?: string;
    };
    const { pathname } = router;

    const isAdminPage = Object.values(ADMIN_ROUTES).includes(pathname);

    const handleOnClickHelp = () => {
        triggerZendeskRedirect(ZENDESK_URLS.HELPCENTER);
        trackEvent(ANALYTICS_EVENTS.NAVIGATE_TO_HELP_CENTER);
    };

    const handleOnClickInstallation = () => {
        router.push({
            pathname: REDIRECT_ROUTES.INSTALLATION,
        });
    };

    if (!isOpen) {
        return null;
    }

    return (
        <Card
            className="absolute z-20 flex-shrink-0 h-full text-sm w-fit max-w-[11rem] md:relative rounded-l-none rounded-r-none shadow-md"
            pt={{
                body: {
                    className: "h-full px-1 py-4",
                },
                content: {
                    className:
                        "h-full flex flex-col gap-12 justify-between overflow-y-auto !p-0",
                },
            }}
        >
            <div>
                <Button
                    className="md:hidden text-default absolute top-2"
                    icon="pi pi-chevron-left"
                    rounded
                    text
                    aria-label="Close Sidebar"
                    onClick={onCloseIconClick}
                />
                <div className="size-full max-h-20">
                    <IconBetterspace type={theme} />
                </div>
                {!!companyId && !!locationId && (
                    <LocationControls
                        companyId={companyId}
                        locationId={locationId}
                    />
                )}
                {!!companyId && !locationId && (
                    <CompanyControls companyId={companyId} />
                )}
                {!companyId && <AdminControls />}
            </div>
            <div className={`flex justify-center flex-col mt-auto gap-2 px-4`}>
                {isAdminPage && (
                    <Button
                        outlined
                        size="small"
                        icon={"pi pi-wrench"} // Not included in the PrimeIcons export
                        label={t("common:installation") || ""}
                        onClick={handleOnClickInstallation}
                    />
                )}
                <Button
                    className="!border-accent-default text-accent-default !shadow-none dark:text-default justify-center"
                    outlined
                    size="small"
                    icon={PrimeIcons.QUESTION}
                    label={t("common:help") || ""}
                    onClick={handleOnClickHelp}
                    pt={{
                        label: {
                            className: "flex-none",
                        },
                    }}
                />
                <div className="text-center text-xs flex-nowrap">
                    {dayjs().year()} Betterspace GmbH
                </div>
            </div>
        </Card>
    );
};

export default Sidebar;
